import React from 'react';
import { motion } from 'framer-motion';
import { useApi } from '../hooks/useApi';
import { apiService, getCurrentLocale } from '../services/api';
import { ArrowRight } from './icons';
import { SlotMachineButton } from './SlotMachineButton';
import { getBestImageUrl } from '../utils/imageUrl';

const useIsMobile = (query: string = '(max-width: 768px)') => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handleChange = () => setIsMobile(mediaQuery.matches);

    // Initial check
    handleChange();

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [query]);

  return isMobile;
};


interface CTAButton {
  text: string;
  url: string;
  variant?: 'primary' | 'secondary' | 'tertiary';
  isExternal?: boolean;
}

interface HeroSectionData {
  title?: string;
  subtitle?: string;
  description?: string;
  overlayOpacity?: number;
  textColor?: string;
  primaryButton?: CTAButton;
  backgroundImage?: {
    url?: string;
    alternativeText?: string;
    data?: {
      attributes?: {
        url?: string;
      };
    };
  };
}

function HeroSection(): React.JSX.Element {
  // Resolve current locale from URL/localStorage and refetch on change
  const locale = getCurrentLocale();
  const { data, loading, error } = useApi(apiService.getHeroSection, [locale]) as {
    data: HeroSectionData | null;
    loading: boolean;
    error: any;
  };
  const isMobile = useIsMobile();

  // Removed useEffect logging to prevent React error #310


  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  // Show loading state
  if (loading) {
    return (
      <section className="hero" id="home">
        <div className="hero-background">
          <div className="loading-placeholder">Loading hero image...</div>
          <div className="hero-overlay"></div>
        </div>
        <div className="container-hero">
          <div className="hero-content">
            <div className="loading-placeholder">
              <div className="loading-title"></div>
              <div className="loading-button"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state with fallback content
  if (error) {
    return (
      <section className="hero" id="home">
        <div className="hero-background">
          <div className="loading-placeholder">Error loading hero image</div>
          <div className="hero-overlay" style={{ opacity: 0.4 }}></div>
        </div>
        <div className="container-hero">
          <div className="hero-content" style={{ color: 'white' }}>
            <h1 className="hero-title">
              De kliniek voor haar- en hoofdhuidproblemen
            </h1>
            <div className="hero-actions">
              <SlotMachineButton
                text="Maak een afspraak"
                icon={<ArrowRight />}
                href="#afspraak"
                className="btn btn-secondary"
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Use data from API or fallback to default - simple variables to avoid re-render issues
  const title = data?.title || 'De kliniek voor haar- en hoofdhuidproblemen';
  const subtitle = data?.subtitle || '';
  const description = data?.description || '';
  const overlayOpacity = data?.overlayOpacity || 0.4;
  const textColor = data?.textColor || 'white';
  const ctaButton = data?.primaryButton || { text: 'Maak een afspraak', url: '#afspraak', variant: 'secondary' };
  
          // Use the best available image format (handle both flat and nested structures)
        const bgSource = (data as any)?.backgroundImage?.data?.attributes || (data as any)?.backgroundImage;
        const backgroundImageUrl = getBestImageUrl(bgSource as any);
        const backgroundImageAlt = (data as any)?.backgroundImage?.alternativeText || 'Hero Background';

  // Direct logging without useEffect to avoid React error #310
  if (data && !loading && !error) {
    console.log('🏠 HeroSection - ✅ Strapi data loaded:', !!data);
    console.log('🏠 HeroSection - Full data object:', data);
    console.log('🏠 HeroSection - Available keys:', Object.keys(data || {}));
    console.log('🏠 HeroSection - Background image structure:', data.backgroundImage);
    console.log('🏠 HeroSection - Computed backgroundImageUrl:', backgroundImageUrl);
  }

  return (
    <section className="hero" id="home" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="hero-background">
                      {backgroundImageUrl ? (
                <img 
                  src={backgroundImageUrl} 
                  alt={backgroundImageAlt} 
                  className="hero-bg-img"
                />
              ) : (
                <div className="loading-placeholder">No hero image available</div>
              )}
        <div 
          className="hero-overlay" 
          style={{ opacity: overlayOpacity }}
        ></div>
      </div>
      
      <div className="container-hero" style={{ alignItems: 'stretch' }}>
        <motion.div 
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ color: textColor, textAlign: locale === 'ar' ? 'right' : 'left' }}
        >
          <motion.h1 className="hero-title" variants={itemVariants}>
            {title}
          </motion.h1>
          {subtitle && (
            <motion.p className="hero-subtitle" variants={itemVariants}>
              {subtitle}
            </motion.p>
          )}
          {description && (
            <motion.div 
              className="hero-description" 
              variants={itemVariants}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          <motion.div className="hero-actions" variants={itemVariants}>
            <SlotMachineButton
              text={ctaButton.text}
              icon={<ArrowRight />}
              href={ctaButton.url}
              className={`btn btn-secondary`}
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default HeroSection;